// OS DIÁLOGOS DA REGRA DE MARGEM — cadastrar e encerrar (ADR-0012, etapa 2).
//
// A margem é lei do ente. Até 15/09/2026 o sistema aplicava 35/5/5 a toda
// prefeitura, e a Lei 2.231/2024 de Morada Nova manda 40/5/15 com o somatório do
// crédito consignado em 60%. Este formulário é por onde essa lei entra.
//
// PONTOS PERCENTUAIS NA TELA, FRAÇÃO NA API. Quem preenche digita `40`, porque é
// assim que a lei está escrita; a API recebe `0.4`, porque é assim que a conta
// usa. A conversão mora aqui, num lugar só, e a API recusa qualquer valor acima
// de 1 — de modo que um `40` que escape por outro caminho vira erro, e nunca uma
// margem quarenta vezes maior.
//
// NÃO HÁ EXCLUSÃO (ADR-0009): encerrar é gravar a última competência. "Com que
// margem esta averbação passou em agosto?" é pergunta de auditoria, e apagar a
// linha destrói a resposta.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Modal, SelectField, TextField, TextareaField, fmtBRL } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { competenciaCorrente, competenciaValida } from "./relacao-campos";
import {
  TabelaServicosDaRegra,
  mapaInicial,
  servicosDoMapa,
  usaTetoSomado,
  type MapaDestinos,
} from "./regra-margem-servicos";
import type {
  AdminDestinoMargem,
  AdminPrefeitura,
  BaseDeMargem,
  EnsaioRegraMargem,
  ImpedimentoBaseLegal,
  OrdemPrioridadeDeCorte,
  PoliticaDeCorteDto,
  QuemCortaNoTeto,
  RegraDeMargemDto,
} from "@atlas/sdk";

type EnsaioValido = Extract<EnsaioRegraMargem, { medicao_valida: true }>;

const ROTULO_PRODUTO = {
  EMPRESTIMO: "Empréstimo",
  CARTAO_CONSIGNADO: "Cartão consignado",
  CARTAO_BENEFICIOS: "Cartão de benefício",
  TOTAL: "Somatório",
} as const;
const PRODUTOS = ["EMPRESTIMO", "CARTAO_CONSIGNADO", "CARTAO_BENEFICIOS"] as const;

/** As duas bases (#273), como a tela as nomeia. */
export const ROTULO_BASE: Record<BaseDeMargem, string> = {
  informada: "Salário líquido informado na carga",
  liquida_legal: "Base líquida legal (contracheque)",
  margem_da_folha: "Margem de empréstimo informada na carga",
};

/** Quem sai primeiro quando a margem não comporta (#277). */
export const ROTULO_ORDEM: Record<OrdemPrioridadeDeCorte, string> = {
  antiguidade: "Antiguidade — sai a mais recente",
  tipo_servico: "Tipo de serviço — sai a de maior prioridade",
};

/** Quem aplica o teto global (#277). */
export const ROTULO_QUEM_CORTA: Record<QuemCortaNoTeto, string> = {
  folha_do_orgao: "A folha do órgão (a Atlas só avisa)",
  atlas: "A Atlas, na remessa",
};

/** O teto global em uma linha, para a ficha e o histórico (#277). */
export function resumoDoTeto(p: PoliticaDeCorteDto | undefined): string {
  if (!p || p.tetoGlobal == null) return "Sem teto global";
  const pct = `${Math.round(p.tetoGlobal * 1000) / 10}%`;
  return `${pct} · ${p.ordem === "antiguidade" ? "antiguidade" : "tipo de serviço"}${p.descontoParcial ? " · parcial" : ""} · corta: ${
    p.quemCorta === "atlas" ? "Atlas" : "folha do órgão"
  }`;
}

/** O que o portão da base legal recusou, em português (#273). */
export function textoDoImpedimento(i: ImpedimentoBaseLegal): string {
  switch (i.codigo) {
    case "sem_competencia_fechada":
      return "Nenhuma competência fechada neste órgão: não há onde medir a base.";
    case "sem_servidores_ativos":
      return "Nenhum servidor ativo neste órgão.";
    case "cobertura_insuficiente":
      return `Só ${(i.cobertura * 100).toFixed(1)}% dos servidores ativos têm base legal (mínimo ${(i.minimo * 100).toFixed(0)}%): ${i.totalSemBase} sem base — sem holerite ou com rubrica a classificar.`;
    case "sem_desconto_obrigatorio":
      return "Nenhuma rubrica classificada como desconto obrigatório: a base legal seria só a soma dos proventos, e a margem subiria em vez de cair.";
    case "agregacao_sem_cache":
      return "A base líquida legal ainda não pode ser ligada em nenhum órgão: a agregação do contracheque não tem cache (pendência 29). Esta conferência vale como diagnóstico.";
  }
}

/** Os impedimentos de um 422 `base_legal_impedida`; vazio em qualquer outro erro. */
function impedimentosDo(e: unknown): ImpedimentoBaseLegal[] {
  const x = e as { code?: string; details?: { impedimentos?: ImpedimentoBaseLegal[] } } | null;
  return x?.code === "base_legal_impedida" ? (x.details?.impedimentos ?? []) : [];
}

const MIN_JUSTIFICATIVA = 3;
const HINT_JUSTIFICATIVA = "3 a 500 caracteres. Fica no histórico e na auditoria.";
const HINT_FUNDAMENTO = 'A norma e o artigo. Ex.: "Lei Municipal 2.231/2024, art. 4º, parágrafo único".';

/** Toda gravação invalida a ficha da organização e a lista de regras. */
function useInvalidarRegras() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["admin", "regra-margem"] });
    void qc.invalidateQueries({ queryKey: ["admin", "regras-margem"] });
  };
}

/** `"40"` vira `0.4`. Vírgula aceita: quem digita percentual em pt-BR usa vírgula. */
function fracaoDe(texto: string): number | null {
  const n = Number(texto.trim().replace(",", "."));
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  // Duas casas em pontos percentuais (37,5%) cabem em quatro na fração.
  return Math.round(n * 100) / 10000;
}

const pontosDe = (fracao: number): string => String(Math.round(fracao * 10000) / 100);

function Erro({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        border: "1px solid var(--danger-500)",
        borderRadius: 8,
        padding: "10px 12px",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

// ============================================================ o ensaio

const fmtN = (n: number) => n.toLocaleString("pt-BR");
const fmtHora = (iso: string) =>
  new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

/**
 * O que a regra proposta faz com a folha — a medição que autoriza o cadastro.
 *
 * Duas leituras, na ordem em que decidem: primeiro quem fica ACIMA do novo
 * limite (é o número que o cadastro exige de volta), separando quem já passava
 * do limite atual de quem passa a passar por causa da regra; depois os
 * somatórios por produto, antes e depois. A lista de matrículas é cortada pela
 * API; os contadores, não.
 */
function PainelEnsaio({ r }: { r: EnsaioValido }) {
  const e = r.ensaio;
  const acima = e.acimaDoNovoLimite;
  return (
    <div
      role="region"
      aria-label="Ensaio da regra"
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: "10px 12px",
        display: "grid",
        gap: 10,
      }}
    >
      <div style={{ fontSize: 13 }}>
        <strong>{fmtN(e.vinculos)} vínculos medidos</strong>
        {e.salarioZero > 0 ? (
          <span style={{ color: "var(--text-muted)" }}> ({fmtN(e.salarioZero)} com salário zero)</span>
        ) : null}
        {r.regra_proposta.base === "liquida_legal" && (e.baseZero?.depois ?? 0) > 0 ? (
          <div style={{ color: "var(--danger, #DC2626)", marginTop: 4 }}>
            {fmtN(e.baseZero!.depois)} ficam sem margem: não têm base legal (sem contracheque, ou com rubrica a
            classificar).
          </div>
        ) : null}
        <span style={{ color: "var(--text-dim)" }}> · às {fmtHora(r.medido_em)}</span>
        {r.regra_atual.padrao ? (
          <span style={{ color: "var(--text-muted)" }}> · hoje vale o padrão do sistema</span>
        ) : null}
      </div>

      <div style={{ fontSize: 13 }}>
        <strong>Acima do novo limite: {fmtN(acima.vinculos)} vínculos</strong>
        <span style={{ color: "var(--text-muted)" }}>
          {" "}
          ({fmtN(acima.novos)} por efeito da regra nova · excesso total {fmtBRL(acima.excessoTotal)})
        </span>
        {acima.itens.length > 0 ? (
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, display: "grid", gap: 2, fontSize: 12 }}>
            {acima.itens.slice(0, 10).map((i) => (
              <li key={`${i.matricula}-${i.produto}`}>
                <span style={{ fontFamily: "var(--mono)" }}>{i.matricula}</span> · {ROTULO_PRODUTO[i.produto]}{" "}
                · {fmtBRL(i.comprometido)} de {fmtBRL(i.limiteNovo)} (excesso {fmtBRL(i.excesso)})
                {i.jaAcimaDoAtual ? <span style={{ color: "var(--text-dim)" }}> · já acima hoje</span> : null}
              </li>
            ))}
            {acima.itensTotal > Math.min(10, acima.itens.length) ? (
              <li style={{ color: "var(--text-dim)", listStyle: "none" }}>
                e mais {fmtN(acima.itensTotal - Math.min(10, acima.itens.length))}
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>

      <table style={{ fontSize: 12, borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr style={{ color: "var(--text-dim)", textAlign: "left" }}>
            <th style={{ fontWeight: 600, padding: "2px 0" }}>Produto</th>
            <th style={{ fontWeight: 600 }}>Teto hoje → novo</th>
            <th style={{ fontWeight: 600 }}>Disponível hoje → novo</th>
            <th style={{ fontWeight: 600 }}>Ganham / perdem / iguais</th>
          </tr>
        </thead>
        <tbody>
          {PRODUTOS.map((p) => (
            <tr key={p}>
              <td style={{ padding: "2px 0" }}>{ROTULO_PRODUTO[p]}</td>
              <td>
                {fmtBRL(e.antes.totalPorProduto[p])} → {fmtBRL(e.depois.totalPorProduto[p])}
              </td>
              <td>
                {fmtBRL(e.antes.disponivelPorProduto[p])} → {fmtBRL(e.depois.disponivelPorProduto[p])}
              </td>
              <td>
                {fmtN(e.porProduto[p].ganham)} / {fmtN(e.porProduto[p].perdem)} /{" "}
                {fmtN(e.porProduto[p].iguais)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================ cadastrar

export function NovaRegraMargemModal({
  org,
  aberto,
  onFechar,
}: {
  org: AdminPrefeitura;
  aberto: boolean;
  onFechar: () => void;
}) {
  const invalidar = useInvalidarRegras();
  // O padrão do sistema como ponto de partida: quem vai mudar só um produto não
  // precisa redigitar os outros dois, e quem esquecer de mexer grava o que já
  // valia — nunca um zero acidental.
  const [emprestimo, setEmprestimo] = useState("35");
  const [cartao, setCartao] = useState("5");
  const [beneficio, setBeneficio] = useState("5");
  const [total, setTotal] = useState("");
  const [inicio, setInicio] = useState(competenciaCorrente());
  const [fim, setFim] = useState("");
  const [fundamento, setFundamento] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  // O ENSAIO ANTES DE ATIVAR (ADR-0012 §6). Cadastrar exige uma medição recente
  // feita com ESTES percentuais: a chave guarda com quais, e mudar um campo
  // invalida a medição que existe — o operador ensaia de novo, e o botão de
  // cadastrar volta a travar até lá.
  const [ensaio, setEnsaio] = useState<{ chave: string; resultado: EnsaioValido } | null>(null);
  // O que cada tipo de serviço consome (#271), começando pelo padrão do catálogo.
  const [destinos, setDestinos] = useState<MapaDestinos>(() => mapaInicial());
  // Sobre o que o percentual incide (#273). A base não se corrige depois: trocar
  // é criar outra regra.
  const [base, setBase] = useState<BaseDeMargem>("informada");
  // O teto global na emissão (#277). Não muda a margem da averbação — e por
  // isso não entra no ensaio: vale só quando o movimento é montado.
  const [tetoGlobal, setTetoGlobal] = useState("");
  const [ordem, setOrdem] = useState<OrdemPrioridadeDeCorte>("antiguidade");
  const [quemCorta, setQuemCorta] = useState<QuemCortaNoTeto>("folha_do_orgao");
  // Parcial so' existe quando a Atlas corta: a folha do orgao segue a propria lei.
  const [descontoParcial, setDescontoParcial] = useState(false);
  const [impedimentos, setImpedimentos] = useState<ImpedimentoBaseLegal[]>([]);

  const pcts = {
    EMPRESTIMO: fracaoDe(emprestimo),
    CARTAO_CONSIGNADO: fracaoDe(cartao),
    CARTAO_BENEFICIOS: fracaoDe(beneficio),
  };
  const totalFracao = total.trim() === "" ? null : fracaoDe(total);
  const totalInvalido = total.trim() !== "" && totalFracao === null;
  // Serviço no teto somado sem teto: a API recusaria (`destino_total_sem_teto`).
  const totalSemTeto = usaTetoSomado(destinos) && totalFracao === null;
  const percentuaisValidos =
    pcts.EMPRESTIMO != null &&
    pcts.CARTAO_CONSIGNADO != null &&
    pcts.CARTAO_BENEFICIOS != null &&
    !totalInvalido &&
    !totalSemTeto;
  const servicos = servicosDoMapa(destinos);
  // O mapa entra na chave: mudar o que um serviço consome invalida a medição,
  // como mudar um percentual — o número confirmado é medido com a regra inteira.
  const chaveDaProposta = JSON.stringify({ ...pcts, total: totalFracao, servicos, base });
  const ensaioVigente = ensaio && ensaio.chave === chaveDaProposta ? ensaio.resultado : null;
  const tetoGlobalFracao = tetoGlobal.trim() === "" ? null : fracaoDe(tetoGlobal);
  const tetoGlobalInvalido = tetoGlobal.trim() !== "" && (tetoGlobalFracao === null || tetoGlobalFracao === 0);
  const podeGravar =
    !tetoGlobalInvalido &&
    percentuaisValidos &&
    ensaioVigente != null &&
    competenciaValida(inicio) &&
    (fim.trim() === "" || competenciaValida(fim)) &&
    fundamento.trim().length >= MIN_JUSTIFICATIVA &&
    justificativa.trim().length >= MIN_JUSTIFICATIVA;

  const percentuais = () => ({
    EMPRESTIMO: pcts.EMPRESTIMO!,
    CARTAO_CONSIGNADO: pcts.CARTAO_CONSIGNADO!,
    CARTAO_BENEFICIOS: pcts.CARTAO_BENEFICIOS!,
  });

  const ensaiar = useMutation({
    mutationFn: () =>
      atlas.admin.regrasMargem.ensaiar({
        unidadeId: org.id,
        percentuais: percentuais(),
        percentualTotal: totalFracao,
        servicos,
        base,
      }),
    onSuccess: (r) => {
      // Medição inválida vem com status de erro e cai no `onError`; aqui só
      // chega a válida — o tipo estreita por garantia, não por confiança.
      if (r.medicao_valida) setEnsaio({ chave: chaveDaProposta, resultado: r });
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  const gravar = useMutation({
    mutationFn: () =>
      atlas.admin.regrasMargem.criar({
        unidadeId: org.id,
        percentuais: percentuais(),
        percentualTotal: totalFracao,
        competenciaInicio: inicio.trim(),
        competenciaFim: fim.trim() === "" ? null : fim.trim(),
        fundamento: fundamento.trim(),
        justificativa: justificativa.trim(),
        ensaio: {
          medidoEm: ensaioVigente!.medido_em,
          vinculosAcimaDoNovoLimite: ensaioVigente!.ensaio.acimaDoNovoLimite.vinculos,
        },
        servicos,
        base,
        // #277. O desconto parcial só vai quando a Atlas corta: o CHECK `regras_margem_parcial_atlas_ck` recusa o resto.
        ...(tetoGlobalFracao != null
          ? {
              tetoGlobal: tetoGlobalFracao,
              ordemPrioridade: ordem,
              quemCorta,
              ...(quemCorta === "atlas" ? { descontoParcial } : {}),
            }
          : {}),
      }),
    onSuccess: () => {
      invalidar();
      onFechar();
    },
    onError: (e) => {
      setErro(mensagemDeErro(e));
      setImpedimentos(impedimentosDo(e));
      // Qualquer recusa derruba o ensaio: se foi `medicao_desatualizada` ou
      // `medicao_divergente`, a saída é medir de novo; se foi outra coisa, medir
      // de novo custa um clique e nunca é errado.
      setEnsaio(null);
    },
  });

  return (
    <Modal
      open={aberto}
      onClose={onFechar}
      title="Cadastrar regra de margem"
      // `lg`: a tabela de serviços (#271) tem quatro colunas e não cabe nos 560px do `md`.
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setErro(null);
              ensaiar.mutate();
            }}
            disabled={!percentuaisValidos || ensaiar.isPending}
          >
            {ensaiar.isPending ? "Medindo…" : ensaioVigente ? "Ensaiar de novo" : "Ensaiar"}
          </Button>
          <Button
            onClick={() => {
              setErro(null);
              setImpedimentos([]);
              gravar.mutate();
            }}
            disabled={!podeGravar || gravar.isPending}
            title={ensaioVigente ? undefined : "Ensaie com estes percentuais antes de cadastrar"}
          >
            {gravar.isPending ? "Gravando…" : "Cadastrar"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {erro ? <Erro>{erro}</Erro> : null}
        {impedimentos.length > 0 ? (
          <ul aria-label="O que falta para a base legal" style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: "grid", gap: 4 }}>
            {impedimentos.map((i) => (
              <li key={i.codigo}>{textoDoImpedimento(i)}</li>
            ))}
          </ul>
        ) : null}
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
          Os percentuais valem para <strong>{org.nome}</strong> a partir da competência de início. A regra não
          alcança nenhuma outra organização — a lei do município vale para a administração indireta, mas cada
          autarquia precisa da regra dela.
        </p>

        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}
        >
          <TextField
            label="Empréstimo (%)"
            value={emprestimo}
            onChange={(e) => setEmprestimo(e.target.value)}
            error={pcts.EMPRESTIMO == null ? "0 a 100" : undefined}
          />
          <TextField
            label="Cartão de crédito consignado (%)"
            value={cartao}
            onChange={(e) => setCartao(e.target.value)}
            error={pcts.CARTAO_CONSIGNADO == null ? "0 a 100" : undefined}
          />
          <TextField
            label="Cartão de benefício (%)"
            value={beneficio}
            onChange={(e) => setBeneficio(e.target.value)}
            error={pcts.CARTAO_BENEFICIOS == null ? "0 a 100" : undefined}
          />
        </div>

        <SelectField
          label="Base de cálculo"
          value={base}
          onChange={(e) => setBase(e.target.value as BaseDeMargem)}
          options={(Object.keys(ROTULO_BASE) as BaseDeMargem[]).map((b) => ({ value: b, label: ROTULO_BASE[b] }))}
          hint={
            base === "liquida_legal"
              ? "Proventos que compõem a base menos os descontos obrigatórios, do contracheque gravado. Quem não tem base legal fica sem margem. Só passa com 95% dos ativos cobertos — confira na aba Margem."
              : base === "margem_da_folha"
                ? "A coluna de salário líquido da carga já traz a margem de empréstimo calculada pela folha, e ela vale como veio. Cartões e teto somado saem dela na proporção dos percentuais (5% de uma margem de 30% = 1/6). Sem teto global."
                : "A coluna de salário líquido da carga, como sempre foi."
          }
        />

        <TextField
          label="Teto somado do crédito consignado (%)"
          value={total}
          onChange={(e) => setTotal(e.target.value)}
          hint="Opcional. Limite do somatório dos três produtos — em Morada Nova, 60. Vazio = só os limites por produto."
          error={totalInvalido ? "0 a 100, ou vazio" : undefined}
        />

        <TextField
          label="Teto global da emissão (%)"
          value={tetoGlobal}
          onChange={(e) => setTetoGlobal(e.target.value)}
          hint="Opcional. Obrigatórios mais os descontos do movimento até este percentual da base — no Decreto 8.690/2016, 70. Vale na emissão, não na averbação. Vazio = sem teto."
          error={tetoGlobalInvalido ? "1 a 100, ou vazio" : undefined}
        />
        {tetoGlobalFracao != null ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <SelectField
              label="Quem sai primeiro"
              value={ordem}
              onChange={(e) => setOrdem(e.target.value as OrdemPrioridadeDeCorte)}
              options={(Object.keys(ROTULO_ORDEM) as OrdemPrioridadeDeCorte[]).map((o) => ({ value: o, label: ROTULO_ORDEM[o] }))}
            />
            <SelectField
              label="Quem corta"
              value={quemCorta}
              onChange={(e) => setQuemCorta(e.target.value as QuemCortaNoTeto)}
              options={(Object.keys(ROTULO_QUEM_CORTA) as QuemCortaNoTeto[]).map((q) => ({ value: q, label: ROTULO_QUEM_CORTA[q] }))}
              hint={
                quemCorta === "atlas"
                  ? "A Atlas suspende na remessa quem sai primeiro até caber no teto. O retorno registra a suspensa como suspensa pelo teto — não como falha."
                  : "O movimento sai sem corte, e a emissão avisa quantos servidores excederiam o teto. Use quando a folha do órgão já corta."
              }
            />
          </div>
        ) : null}
        {tetoGlobalFracao != null && quemCorta === "atlas" ? (
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 14 }}>
            <input type="checkbox" checked={descontoParcial} onChange={(e) => setDescontoParcial(e.target.checked)} />
            <span>
              Desconto parcial — a última a sair desconta o que couber, em vez de ser suspensa. A diferença fica como resíduo,
              que a consignatária cobra por fora.
            </span>
          </label>
        ) : null}

        <TabelaServicosDaRegra
          mapa={destinos}
          onMudar={(tipo, destino) => setDestinos((m) => ({ ...m, [tipo]: destino }))}
        />
        {totalSemTeto ? (
          <Erro>Um serviço vai para o teto somado, mas a regra não tem teto somado. Informe o teto ou mude o destino.</Erro>
        ) : null}

        {ensaioVigente ? (
          <PainelEnsaio r={ensaioVigente} />
        ) : (
          <p style={{ margin: 0, fontSize: 12, color: "var(--text-dim)" }}>
            {ensaio
              ? "Os percentuais mudaram depois da medição (ou o que um serviço consome). Ensaie de novo para cadastrar."
              : "Ensaie antes de cadastrar: a medição mostra, servidor a servidor, o que muda e quem já está acima do novo limite."}
          </p>
        )}

        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}
        >
          <TextField
            label="Competência de início"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
            hint="AAAAMM"
            error={competenciaValida(inicio) ? undefined : "AAAAMM"}
          />
          <TextField
            label="Competência final"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            hint="Opcional. Vazio = sem fim previsto."
            error={fim.trim() === "" || competenciaValida(fim) ? undefined : "AAAAMM"}
          />
        </div>

        <TextField
          label="Fundamento"
          value={fundamento}
          onChange={(e) => setFundamento(e.target.value)}
          hint={HINT_FUNDAMENTO}
        />
        <TextareaField
          label="Justificativa"
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          hint={HINT_JUSTIFICATIVA}
          rows={3}
        />
      </div>
    </Modal>
  );
}

// ============================================================ corrigir os serviços

/**
 * Corrige SÓ o que cada tipo de serviço consome, numa regra vigente ou agendada
 * (#271). Os percentuais continuam sendo decididos no cadastro, com ensaio.
 *
 * COM ENSAIO desde a #272: o destino de um serviço passou a mudar a margem, então
 * corrigir uma regra VIGENTE passa pelo mesmo portão do cadastro — medir com o
 * mapa editado, e confirmar o número que a tela mostrou. Mudar um destino depois
 * de medir invalida a medição. A agendada ainda não vale: salva sem medir.
 */
export function CorrigirServicosRegraModal({
  regra,
  aberto,
  onFechar,
}: {
  regra: RegraDeMargemDto | null;
  aberto: boolean;
  onFechar: () => void;
}) {
  const invalidar = useInvalidarRegras();
  const [destinos, setDestinos] = useState<MapaDestinos>(() => mapaInicial(regra?.servicos));
  const [chaveRegra, setChaveRegra] = useState<number | null>(regra?.id ?? null);
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ensaio, setEnsaio] = useState<{ chave: string; resultado: EnsaioValido } | null>(null);
  // Outra regra aberta no mesmo modal: recomeça do que ELA diz.
  if ((regra?.id ?? null) !== chaveRegra) {
    setChaveRegra(regra?.id ?? null);
    setDestinos(mapaInicial(regra?.servicos));
    setJustificativa("");
    setErro(null);
    setEnsaio(null);
  }

  const totalSemTeto = usaTetoSomado(destinos) && regra?.percentualTotal == null;
  const servicos = regra ? servicosDoMapa(destinos, regra.servicos) : [];
  // Só a vigente exige medição: a agendada foi ensaiada no cadastro e ainda não vale.
  const exigeEnsaio = regra?.situacao === "vigente";
  const chaveDaProposta = JSON.stringify(servicos);
  const ensaioVigente = ensaio && ensaio.chave === chaveDaProposta ? ensaio.resultado : null;
  const podeGravar =
    regra != null &&
    !totalSemTeto &&
    (!exigeEnsaio || ensaioVigente != null) &&
    justificativa.trim().length >= MIN_JUSTIFICATIVA;

  const ensaiar = useMutation({
    mutationFn: () =>
      atlas.admin.regrasMargem.ensaiar({
        unidadeId: regra!.unidadeId,
        percentuais: regra!.percentuais,
        percentualTotal: regra!.percentualTotal,
        servicos,
      }),
    onSuccess: (r) => {
      if (r.medicao_valida) setEnsaio({ chave: chaveDaProposta, resultado: r });
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  const gravar = useMutation({
    mutationFn: () =>
      atlas.admin.regrasMargem.alterar(regra!.id, {
        servicos,
        justificativa: justificativa.trim(),
        ...(ensaioVigente
          ? {
              ensaio: {
                medidoEm: ensaioVigente.medido_em,
                vinculosAcimaDoNovoLimite: ensaioVigente.ensaio.acimaDoNovoLimite.vinculos,
              },
            }
          : {}),
      }),
    onSuccess: () => {
      invalidar();
      onFechar();
    },
    onError: (e) => {
      setErro(mensagemDeErro(e));
      // Recusa de medição (desatualizada, divergente): a saída é medir de novo.
      setEnsaio(null);
    },
  });

  return (
    <Modal
      open={aberto}
      onClose={onFechar}
      title="O que cada serviço consome"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          {exigeEnsaio ? (
            <Button
              variant="ghost"
              onClick={() => {
                setErro(null);
                ensaiar.mutate();
              }}
              disabled={regra == null || totalSemTeto || ensaiar.isPending}
            >
              {ensaiar.isPending ? "Medindo…" : ensaioVigente ? "Ensaiar de novo" : "Ensaiar"}
            </Button>
          ) : null}
          <Button
            onClick={() => {
              setErro(null);
              gravar.mutate();
            }}
            disabled={!podeGravar || gravar.isPending}
          >
            {gravar.isPending ? "Gravando…" : "Salvar"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {erro ? <Erro>{erro}</Erro> : null}
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
          Para cada tipo de serviço, de que margem o desconto sai nesta regra. O que ficar no padrão segue o
          catálogo; os percentuais da regra não mudam aqui.
        </p>
        <TabelaServicosDaRegra
          mapa={destinos}
          onMudar={(tipo: string, destino: AdminDestinoMargem) => setDestinos((m) => ({ ...m, [tipo]: destino }))}
        />
        {totalSemTeto ? (
          <Erro>Um serviço vai para o teto somado, mas esta regra não tem teto somado.</Erro>
        ) : null}
        {exigeEnsaio ? (
          ensaioVigente ? (
            <PainelEnsaio r={ensaioVigente} />
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: "var(--text-dim)" }}>
              {ensaio
                ? "O que um serviço consome mudou depois da medição. Ensaie de novo para salvar."
                : "Esta regra vale hoje: mudar o que um serviço consome muda a margem da folha. Ensaie antes de salvar."}
            </p>
          )
        ) : null}
        <TextareaField
          label="Justificativa"
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          hint={HINT_JUSTIFICATIVA}
          rows={3}
        />
      </div>
    </Modal>
  );
}

// ============================================================ encerrar

/**
 * Encerrar é gravar a última competência em que a regra vale.
 *
 * A competência sugerida é a corrente: encerrar no passado reescreveria a margem
 * de meses em que averbações já foram aprovadas, e isso tem de ser um ato
 * deliberado, digitado.
 */
export function EncerrarRegraMargemModal({
  regra,
  aberto,
  onFechar,
}: {
  regra: RegraDeMargemDto | null;
  aberto: boolean;
  onFechar: () => void;
}) {
  const invalidar = useInvalidarRegras();
  const [fim, setFim] = useState(competenciaCorrente());
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const encerrar = useMutation({
    mutationFn: () =>
      atlas.admin.regrasMargem.alterar(regra!.id, {
        competenciaFim: fim.trim(),
        justificativa: justificativa.trim(),
      }),
    onSuccess: () => {
      invalidar();
      onFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  const podeGravar = competenciaValida(fim) && justificativa.trim().length >= MIN_JUSTIFICATIVA;

  return (
    <Modal
      open={aberto && regra != null}
      onClose={onFechar}
      title="Encerrar regra de margem"
      danger
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setErro(null);
              encerrar.mutate();
            }}
            disabled={!podeGravar || encerrar.isPending}
          >
            {encerrar.isPending ? "Encerrando…" : "Encerrar"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {erro ? <Erro>{erro}</Erro> : null}
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
          A partir da competência seguinte, a organização volta ao padrão do sistema — a não ser que outra
          regra comece a valer. Contratos já averbados não são reavaliados.
        </p>
        <TextField
          label="Última competência em que a regra vale"
          value={fim}
          onChange={(e) => setFim(e.target.value)}
          hint="AAAAMM, inclusiva."
          error={competenciaValida(fim) ? undefined : "AAAAMM"}
        />
        <TextareaField
          label="Justificativa"
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          hint={HINT_JUSTIFICATIVA}
          rows={3}
        />
      </div>
    </Modal>
  );
}

// ============================================================ cancelar

/**
 * Cancelar é só para a regra AGENDADA — que ainda não começou a valer. A que já
 * valeu se encerra: cancelá-la reescreveria a margem com que averbações daquele
 * mês foram aprovadas. A API recusa (`ja_vigorou`) se a tela errar.
 */
export function CancelarRegraMargemModal({
  regra,
  aberto,
  onFechar,
}: {
  regra: RegraDeMargemDto | null;
  aberto: boolean;
  onFechar: () => void;
}) {
  const invalidar = useInvalidarRegras();
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const cancelar = useMutation({
    mutationFn: () => atlas.admin.regrasMargem.cancelar(regra!.id, { justificativa: justificativa.trim() }),
    onSuccess: () => {
      invalidar();
      onFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  return (
    <Modal
      open={aberto && regra != null}
      onClose={onFechar}
      title="Cancelar regra agendada"
      danger
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>
            Voltar
          </Button>
          <Button
            onClick={() => {
              setErro(null);
              cancelar.mutate();
            }}
            disabled={justificativa.trim().length < MIN_JUSTIFICATIVA || cancelar.isPending}
          >
            {cancelar.isPending ? "Cancelando…" : "Cancelar regra"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {erro ? <Erro>{erro}</Erro> : null}
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
          A regra agendada para <strong>{regra?.competenciaInicio}</strong> nunca chegou a valer e deixa de
          existir para a margem. A linha fica no histórico, marcada como cancelada, com quem cancelou e por
          quê.
        </p>
        <TextareaField
          label="Justificativa"
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          hint={HINT_JUSTIFICATIVA}
          rows={3}
        />
      </div>
    </Modal>
  );
}

export { pontosDe };
